export const ORDER_STATUS_OPTIONS = [
  { value: "All", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "paid", label: "Paid" },
  { value: "refunded", label: "Refunded" },
  { value: "failed", label: "Failed" },
  { value: "cancelled", label: "Cancelled" },
  { value: "expired", label: "Expired" },
];

export const normalizeOrderStatus = (status) => {
  const normalized = String(status || "pending").trim().toLowerCase();
  if (
    normalized === "paid"
    || normalized === "refunded"
    || normalized === "failed"
    || normalized === "cancelled"
    || normalized === "expired"
  ) {
    return normalized;
  }
  return "pending";
};

export const statusLabel = (status) => {
  const normalized = normalizeOrderStatus(status);
  if (normalized === "paid") return "Paid";
  if (normalized === "refunded") return "Refunded";
  if (normalized === "failed") return "Failed";
  if (normalized === "cancelled") return "Cancelled";
  if (normalized === "expired") return "Expired";
  return "Pending";
};

export const statusClassName = (status) => {
  const normalized = normalizeOrderStatus(status);
  if (normalized === "paid") return "bg-green-100 text-green-700";
  if (normalized === "pending") return "bg-amber-100 text-amber-700";
  if (normalized === "refunded") return "bg-violet-100 text-violet-700";
  if (normalized === "failed") return "bg-red-100 text-red-700";
  if (normalized === "cancelled") return "bg-gray-100 text-gray-700";
  return "bg-slate-100 text-slate-700";
};

export const isPaidOrder = (status) => normalizeOrderStatus(status) === "paid";
export const isRefundedOrder = (status) => normalizeOrderStatus(status) === "refunded";

export const buildOrderCode = (id) => {
  const source = String(id || "").replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
  return `#ORD-${source.slice(0, 6).padEnd(3, "0")}`;
};

export const quantityFromSelectedTickets = (selectedTickets) => {
  if (!Array.isArray(selectedTickets)) return 0;

  return selectedTickets.reduce((sum, group) => {
    const quantity = Number(group?.quantity);
    if (Number.isFinite(quantity) && quantity > 0) return sum + quantity;

    const seatCount = Array.isArray(group?.seat_labels) ? group.seat_labels.length : 0;
    return sum + seatCount;
  }, 0);
};

export const seatLabelsFromSelectedTickets = (selectedTickets) => {
  if (!Array.isArray(selectedTickets)) return [];

  return [...new Set(
    selectedTickets.flatMap((group) => (
      Array.isArray(group?.seat_labels)
        ? group.seat_labels.map((label) => String(label || "").trim()).filter(Boolean)
        : []
    ))
  )];
};

const normalizeHoldLabel = (value) => String(value || "").trim().toLowerCase();

export const matchHeldSeatsForOrder = (holds, { userId, selectedTickets } = {}) => {
  const labels = new Set(seatLabelsFromSelectedTickets(selectedTickets).map(normalizeHoldLabel));
  const rows = (holds || []).filter((hold) => String(hold?.hold_status || "held").toLowerCase() === "held");

  const forBuyer = userId
    ? rows.filter((hold) => String(hold.reserved_by || "") === String(userId))
    : rows;

  if (labels.size && forBuyer.length) {
    const labeled = forBuyer.filter((hold) => labels.has(normalizeHoldLabel(hold.seat_label)));
    if (labeled.length) return labeled;
  }

  return forBuyer;
};

export const releaseOrderSeatHolds = async (supabase, order) => {
  const eventId = order?.eventId || order?.event_id;
  const userId = order?.userId || order?.user_id;
  if (!supabase || !eventId) return { ok: false, released: 0 };

  let query = supabase
    .from("seat_holds")
    .select("id, seat_label, reserved_by, hold_status")
    .eq("event_id", eventId)
    .eq("hold_status", "held");

  if (userId) {
    query = query.eq("reserved_by", userId);
  }

  const { data: holds, error: loadError } = await query;
  if (loadError) {
    console.error("Unable to load checkout seat holds:", loadError);
    return { ok: false, released: 0, error: loadError };
  }

  const matches = matchHeldSeatsForOrder(holds, {
    userId,
    selectedTickets: order?.selectedTickets || order?.selected_tickets,
  });
  const holdIds = matches.map((hold) => hold.id).filter(Boolean);

  if (!holdIds.length) {
    return { ok: true, released: 0 };
  }

  const { error: updateError } = await supabase
    .from("seat_holds")
    .update({
      hold_status: "released",
      updated_at: new Date().toISOString(),
    })
    .in("id", holdIds);

  if (updateError) {
    console.error("Unable to release checkout seat holds:", updateError);
    return { ok: false, released: 0, error: updateError };
  }

  return { ok: true, released: holdIds.length };
};

export const isPendingOrder = (status) => normalizeOrderStatus(status) === "pending";

export const formatOrderDate = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";

  return parsed.toLocaleDateString("en-PH", {
    timeZone: "Asia/Manila",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

export const formatDateKey = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleDateString("en-CA", { timeZone: "Asia/Manila" });
};

export const formatMoney = (value) => `PHP ${Number(value || 0).toLocaleString()}`;

export const mapPaymentOrder = ({
  order,
  event,
  buyer,
  tickets = [],
  tiersById = {},
  formatTicketId,
  formatSection,
}) => {
  const status = normalizeOrderStatus(order?.status);
  const ticketDetails = (tickets || []).map((ticket) => {
    const tier = tiersById[ticket.ticket_tier_id];
    return {
      id: ticket.id,
      ticketCode: formatTicketId ? formatTicketId(ticket.id) : String(ticket.id),
      tierName: tier?.name || "General Admission",
      exactSection: formatSection
        ? formatSection(tier, ticket)
        : (tier?.section || tier?.name || ""),
      price: Number(tier?.price || 0),
      seatLabel: ticket.seat_label || "",
      isValidated: ticket.is_validated === true,
      refundedAt: ticket.refunded_at || null,
    };
  });

  const selectedQuantity = quantityFromSelectedTickets(order?.selected_tickets);
  const quantity = ticketDetails.length > 0 ? ticketDetails.length : selectedQuantity;

  return {
    id: order.id,
    orderCode: buildOrderCode(order.id),
    customer: buyer?.full_name || buyer?.email || "Guest",
    customerEmail: buyer?.email || "-",
    event: event?.title || "Unknown Event",
    eventDate: event?.date || "",
    eventTime: event?.time || "",
    eventVenue: event?.venue || "-",
    eventId: order.event_id,
    userId: order.user_id,
    selectedTickets: order.selected_tickets || [],
    purchaseDate: order.paid_at || order.created_at,
    quantity,
    total: Number(order.amount || 0),
    status,
    ticketDetails,
  };
};
